import { Hidden } from '@mui/material'
import Tooltip from '@mui/material/Tooltip'
import IonIcon from '@reacticons/ionicons'
import cn from 'classnames'
import detectIncognito from 'detectincognitojs'
import mixpanel from 'mixpanel-browser'
// import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { TailSpin } from 'react-loading-icons'
import useSearchQuery from 'src/components/CustomHooks/SearchQuery'
import { getFingerprint, useFingerprint } from 'src/components/CustomHooks/useFingerprint'
import FreeASKAIPopup from 'src/components/Popup/FreeASKAIPopup'
// import { useIncreaseSolutionCount } from 'src/components/CustomHooks/UseIncreaseSolutionCount'
import {
  AMPLIFY_USER_POOL_APP_CLIENT_ID,
  ASK_AI_NEW_TAB,
  HOMEPAGE_REMOVE_ITEMS,
  MIXPANEL_ENABLED,
  SHOW_ASK_AI,
  SHOW_ASK_AI_TO_ALL,
  SHOW_FREE_ASK_AI,
  SHOW_FREE_SEARCH,
  THEME,
} from 'src/config/config'
import { apolloSdk } from 'src/graphql/apolloSdk'
import {
  IEnum_Content_Contenttype,
  // PhaseItemFragmentDoc,
  useAutosuggestLazyQuery,
  useGetTopicsForHeaderQuery,
  useIsUserInConfigLazyQuery,
} from 'src/graphql/generated/hooks'
import { ensureValidIdToken } from 'src/hooks/tokenRefreshGuard'
import { useAuthenticated } from 'src/hooks/useAuthenticated'

import CustomTooltip from '../CustomTooltip/CustomTooltip'
import { LoadingAnimationHr } from '../LoadingAnimationHr'
import { SmartSearchBox } from '../SmartSearchBox/SmartSearchBox'
import { ThemeOnly } from '../ThemeOnly'
import { CategoryAutosuggestItem } from './Dropdown/CategoryAutosuggestItem'
import { CompanyAutosuggestItem } from './Dropdown/CompanyAutosuggestItem'
import { EventAutosuggestItem } from './Dropdown/EventAutosuggestItem'
import { ProductAutosuggestItem } from './Dropdown/ProductAutosuggestItem'

export interface SearchboxProps {
  className?: string
}

export interface SmartSearchSources {
  description?: string | null
  logo?: string | null
  sourceUrl?: string | null
  title?: string | null
  contentType?: IEnum_Content_Contenttype | null
}

export const Searchbox: React.FC<SearchboxProps> = ({ className = '', ...props }) => {
  const { data: topicData } = useGetTopicsForHeaderQuery({ fetchPolicy: 'cache-first' })
  const [searchText, setSearchText] = useState('')
  const [showPopup, setShowPopup] = useState(false)
  const [searchFocused, setSearchFocused] = useState(false)
  const [isExpand, setIsExpand] = useState(false)
  const [aiSmartSearchId, setAiSmartSearchId] = useState<string>('')
  const [checkIsUserInConfig, { data: permissionData }] = useIsUserInConfigLazyQuery()
  const [checkAskAIPermission, setCheckAskAIPermission] = useState(false)
  const router = useRouter()
  const fingerprintReady = useFingerprint()
  const [isNonLogin, setIsNonLogin] = useState(false)
  const [fingerprint, setFingerprint] = useState<string | null>(null)
  // const { increaseSolutionCount } = useIncreaseSolutionCount()
  const { getSearchQuery } = useSearchQuery()
  const [autosuggest, { data }] = useAutosuggestLazyQuery()
  const productType: string[] = []
  const { session } = useAuthenticated()
  const [token, setToken] = useState<string | null>(null)
  const [isfreeASKAI, setIsfreeASKAI] = useState<boolean | null>(null)
  const [nonLoginPopup, setNonLoginPopup] = useState(false)
  const [loginPopup, setLoginPopup] = useState(false)

  useEffect(() => {
    const idToken = session?.user?.idToken || null
    setToken(idToken)
  }, [session])

  useEffect(() => {
    if (fingerprintReady && SHOW_FREE_ASK_AI) {
      setFingerprint(fingerprintReady)
    }
  }, [fingerprintReady])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const lastAuthUser = localStorage.getItem(
        `CognitoIdentityServiceProvider.${AMPLIFY_USER_POOL_APP_CLIENT_ID}.LastAuthUser`,
      )

      setIsNonLogin(!lastAuthUser)
    }
  }, [])

  useEffect(() => {
    const fetchData = async () => {
      try {
        await checkIsUserInConfig()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in checking that user have ASK AI Permission')
      }
    }
    fetchData()
  }, [])

  useEffect(() => {
    if (permissionData?.isUserInConfig || SHOW_ASK_AI_TO_ALL) {
      setCheckAskAIPermission(permissionData?.isUserInConfig || SHOW_ASK_AI_TO_ALL)
    }
  }, [permissionData])

  const onFocus = () => {
    setSearchFocused(true)
  }
  const onLostFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setTimeout(() => {
        setSearchFocused(false)
      }, 500)
    }
  }
  const onTextChanged = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value ? e.target.value : ''
    setSearchText(text)

    if (text.length > 0) {
      onFocus()
    }

    const fingerprint = await getFingerprint()
    const browser = await detectIncognito()
    const newProductType = (await topicSubTopicIDs(text)) || ''
    const customAccessQuery: string = await getSearchQuery(
      {
        keyword: text,
        audiences: null,
        practiceAreas: null,
        languages: null,
        deployments: null,
        regionsServed: null,
        hqs: null,
        attributes: null,
      },
      newProductType,
    )
    if (SHOW_FREE_SEARCH == true && fingerprint && fingerprint != null && customAccessQuery != '') {
      autosuggest({
        variables: {
          keyword: text,
          accessFreeSearch: true,
          uniqueAddress: !browser?.isPrivate && browser?.browserName !== '' ? fingerprint : '',
          customAccessQuery: customAccessQuery,
        },
        fetchPolicy: 'no-cache',
      })
    } else {
      autosuggest({ variables: { keyword: text } })
    }
    setShowPopup(text.length > 0)

    if (text.length > 0) {
      if (MIXPANEL_ENABLED) {
        mixpanel.track('search_content', {
          keyword: text,
        })
      }
    }
  }

  const topicSubTopicIDs = async (searchText: string = '') => {
    const subTopicFound = topicData?.topics?.some((item: any) =>
      item?.subTopics?.some(
        (sub: any) => sub?.name.toLowerCase() === decodeURIComponent(searchText as string).toLowerCase(),
      ),
    )
    const topicFound = topicData?.topics?.some(
      (item: any) => item?.name.toLowerCase() === decodeURIComponent(searchText as string).toLowerCase(),
    )

    if (searchText != '' && topicData && (topicFound || subTopicFound)) {
      if (
        topicData?.topics?.some(
          (item: any) => item?.name.toLowerCase() === decodeURIComponent(searchText as string).toLowerCase(),
        )
      ) {
        topicData?.topics
          .filter((item: any) => item?.name.toLowerCase() === decodeURIComponent(searchText as string).toLowerCase())
          .map((item) => {
            item?.subTopics?.map((subTopic) => {
              const calculatedId = item?.documentId + '!!' + subTopic?.documentId
              productType.push(calculatedId)
            })
          })
      } else {
        topicData?.topics?.map((item: any) => {
          item?.subTopics
            .filter((sub: any) => sub?.name.toLowerCase() === decodeURIComponent(searchText as string).toLowerCase())
            .map((sub: any) => {
              const calculatedId = item?.documentId + '!!' + sub?.documentId
              productType.push(calculatedId)
            })
        })
      }
    }

    let selection: string[] = []
    if (productType) {
      productType.map((check) => {
        const subTopicDocumentId = check.split('@@@@')[1]
        selection.push(subTopicDocumentId)
      })
    }
    selection = [...new Set(selection)]
    return selection?.join(',')
  }

  // keyword search by enter key
  const handleKeyPress = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const trimmedKeyword = searchText.trim()
      const query: any = {}

      if (trimmedKeyword) {
        query.keyword = trimmedKeyword
      }

      if (productType.length > 0) {
        query.productType = productType.join(',')
      }
      if (trimmedKeyword) {
        query.sort = 'bykeyword'
      }

      await topicSubTopicIDs(searchText)
      if (SHOW_FREE_SEARCH) {
        // increaseSolutionCount(
        //   {
        //     keyword: searchText,
        //     audiences: null,
        //     practiceAreas: null,
        //     languages: null,
        //     deployments: null,
        //     regionsServed: null,
        //     hqs: null,
        //     attributes: null,
        //   },
        //   newProductType,
        // )
      }
      router.push({ pathname: '/search', query: query })
    }
  }

  const increaseSolutionCountAsync = () => {
    if (SHOW_FREE_SEARCH) {
      // increaseSolutionCount({
      //   keyword: searchText,
      //   audiences: null,
      //   practiceAreas: null,
      //   languages: null,
      //   deployments: null,
      //   regionsServed: null,
      //   hqs: null,
      //   attributes: null,
      // })
    }
  }

  const { solutions, categories, events, companies } = data?.autosuggest || {
    solutions: [],
    categories: [],
    events: [],
    companies: [],
  }
  const hasAutosuggest = events.length > 0 || categories.length > 0 || solutions.length > 0 || companies.length > 0

  // keyword search by click button
  const handleRedirect = async () => {
    await topicSubTopicIDs(searchText)
    const productTypeString = productType.join(',')

    const trimmedKeyword = searchText.trim()
    const query: any = {}

    if (trimmedKeyword) {
      query.keyword = trimmedKeyword
    }

    if (productTypeString) {
      query.productType = productTypeString
    }
    if (trimmedKeyword) {
      query.sort = 'bykeyword'
    }

    router.push({
      pathname: '/search',
      query: query,
    })
  }

  const [loading, setLoading] = useState(false)
  const [tableData, setTableData] = useState('')
  const [sources, setSources] = useState<SmartSearchSources[]>([])
  const [errrorMessage, setErrrorMessage] = useState('')
  const [identicalId, setIdenticalId] = useState<number | null>(null)

  useEffect(() => {
    setTableData('')
    setSources([])
  }, [isExpand])

  const fetchdata = async (inputValue: string = '', redirect: boolean = false) => {
    setErrrorMessage('')
    setLoading(true)
    try {
      setTableData('')
      setSources([])
      const browser = await detectIncognito()
      const fingerprintID = fingerprint ? fingerprint : await getFingerprint()
      const queryParams: {
        keyword: string
        accessFreeASKAI?: boolean
        uniqueAddress?: string
        checkCount?: boolean
      } = { keyword: inputValue }
      if (SHOW_FREE_ASK_AI) {
        queryParams.accessFreeASKAI = true
        queryParams.uniqueAddress =
          !browser?.isPrivate && browser?.browserName !== '' && fingerprintID && fingerprintID != null
            ? fingerprintID
            : ''
        queryParams.checkCount = true
      }
      const currentToken = token || session?.user?.idToken || null
      const authToken = currentToken ? (await ensureValidIdToken()) || currentToken : null
      const { aiSmartSearch } = authToken
        ? await apolloSdk.aiSmartSearch(queryParams, { Authorization: `Bearer ${authToken}` })
        : await apolloSdk.aiSmartSearch(queryParams)

      if (SHOW_FREE_ASK_AI && aiSmartSearch?.success) {
        if (aiSmartSearch?.isFreeASKAI == false) {
          setIsfreeASKAI(aiSmartSearch?.isFreeASKAI)
          setLoading(false)
          if (isNonLogin) {
            setNonLoginPopup(true)
          } else {
            setLoginPopup(true)
          }
        } else {
          if (aiSmartSearch?.isFreeASKAI && redirect) {
            router.push({ pathname: '/smartsearch' })
          }
          setIsfreeASKAI(aiSmartSearch?.isFreeASKAI || null)
        }
      }
      if (aiSmartSearch?.data?.id) {
        setAiSmartSearchId(aiSmartSearch?.data?.id)
      }
      if (aiSmartSearch?.data?.identicalId) {
        setIdenticalId(aiSmartSearch?.data?.identicalId)
      }
      if (aiSmartSearch && aiSmartSearch.success && aiSmartSearch.data) {
        setTableData(aiSmartSearch.data.result)
        if (aiSmartSearch.data.sources) {
          setSources(aiSmartSearch.data.sources as any)
        }
      } else {
        setTableData('')
        setSources([])
        setErrrorMessage('Sorry - I wasn’t able to find an answer. Please try a different question.')
      }

      setTimeout(() => {
        setLoading(false)
      }, 2000)
    } catch (error) {
      setTableData('')
      setSources([])
      setErrrorMessage('Sorry - I wasn’t able to find an answer. Please try a different question.')
      setLoading(false)
    }
  }

  const tooltipTitle = (
    <i>
      Ask questions about solutions, security certification, features, comparisons between products, legal technology
      within a particular region, and more.
      <br />
      <br />
      Example: <br /> "What are some leading solutions in [X category]?" or "Is [product] SOC 2 certified?"
    </i>
  )

  return (
    <>
      {SHOW_FREE_ASK_AI && isExpand ? (
        <SmartSearchBox
          fromPage={'homepage'}
          fetchdata={fetchdata}
          className="transition-[all .3s .4s] md:mt-[2.75rem] mt-[1.25rem] mb-1 w-[753px]"
        />
      ) : (
        <div className={cn('relative', className)} onFocus={onFocus} onBlur={onLostFocus}>
          <div
            className={cn(
              'z-20 relative rounded-[1.875rem] border-2 bg-white border-solid flex flex-row items-center pl-[1.3125rem] pr-[0.75rem] h-[60px]',
              {
                'border-turquoise': searchFocused,
                'shadow-md': searchFocused,
                'border-white': !searchFocused,
              },
              { 'text-darkgray2': THEME === 'ilta' },
              { 'text-textdark': THEME === 'legaltech' },
            )}
            {...props}
          >
            <IonIcon name="search" className="mr-2 w-[0.9225rem] h-[0.9225rem]" />
            <input
              autoFocus
              type="text"
              className={cn(
                'focus:outline-none grow text-[14px] mr-[14px] leading-[42px]',
                { 'placeholder-darkgray2 font-secondary font-normal': THEME === 'ilta' },
                { 'placeholder-textdark font-primary font-medium': THEME === 'legaltech' },
              )}
              placeholder="Search by keyword, product name, etc."
              onChange={onTextChanged}
              onKeyPress={handleKeyPress}
            />
            <button onClick={handleRedirect}>
              {searchText === '' ? (
                <Tooltip title="Search all results" placement="right">
                  <div
                    className={cn(
                      'rounded-full flex flex-none bg-turquoise w-[2.5rem] h-[2.5rem] cursor-pointer items-center justify-center',
                      { 'text-white': THEME === 'ilta' },
                    )}
                  >
                    <IonIcon
                      className={cn('mr-0 !w-[20px] !h-[20px]', {
                        'text-white': THEME === 'ilta',
                      })}
                      name="arrow-forward-sharp"
                    />
                  </div>
                </Tooltip>
              ) : (
                <div className="rounded-full flex flex-none bg-turquoise w-[2.5rem] h-[2.5rem] cursor-pointer items-center justify-center">
                  <IonIcon
                    className={cn('mr-0 !w-[20px] !h-[20px]', {
                      'text-white': THEME === 'ilta',
                    })}
                    name="arrow-forward-sharp"
                  />
                </div>
              )}
            </button>
          </div>
          <div
            className={cn(
              'absolute z-10 top-0 rounded-[1.875rem] bg-white shadow-md w-full max-h-[20rem] pt-[4.875rem] pb-8 overflow-hidden flex flex-col',
              {
                hidden: !(showPopup && hasAutosuggest && searchFocused),
              },
            )}
          >
            <div
              className="w-full flex-1 overflow-y-scroll flex flex-col items-center"
              tabIndex={0}
              role="listbox"
              aria-label="Search suggestions"
            >
              <div className="w-full">
                <button
                  onClick={handleRedirect}
                  className="w-full bg-white hover:bg-gray-400 cursor-pointer px-8 text-textdark font-primary font-normal text-[.875rem] leading-7"
                >
                  Search for "{searchText}"
                </button>
                <div className="w-full px-8 mt-3 mb-2 h-px">
                  <div className="w-full bg-navy opacity-20 h-[1px]" />
                </div>
              </div>

              {categories.length > 0 && (
                <>
                  <div className="w-full">
                    <p className="w-full px-8 font-primary font-normal text-darkgray text-[.8125rem] leading-8">
                      Categories
                    </p>
                    {categories.map((data, idx: number) => (
                      <CategoryAutosuggestItem data={data} search={searchText} key={idx} />
                    ))}
                  </div>
                  <div className="w-full px-8 mt-3 mb-2 h-px">
                    <div className="w-full bg-navy opacity-20 h-[1px]" />
                  </div>
                </>
              )}

              {solutions.length > 0 && (
                <>
                  <div className="w-full">
                    <p className="w-full px-8 font-primary font-normal text-darkgray text-[.8125rem] leading-8">
                      Solutions
                    </p>
                    {solutions.map((data, idx: number) => (
                      <ProductAutosuggestItem
                        data={data}
                        search={searchText}
                        key={idx}
                        increaseSolutionCountAsync={increaseSolutionCountAsync}
                      />
                    ))}
                  </div>
                  <div className="w-full px-8 mt-3 mb-2 h-px">
                    <div className="w-full bg-navy opacity-20 h-[1px]" />
                  </div>
                </>
              )}

              {companies.length > 0 && (
                <>
                  <div className="w-full">
                    <p className="w-full px-8 font-primary font-normal text-darkgray text-[.8125rem] leading-8">
                      Companies
                    </p>
                    {companies.map((data, idx: number) => (
                      <CompanyAutosuggestItem data={data} search={searchText} key={idx} />
                    ))}
                  </div>
                  <div className="w-full px-8 mt-3 mb-2 h-px">
                    <div className="w-full bg-navy opacity-20 h-[1px]" />
                  </div>
                </>
              )}

              <ThemeOnly theme="legaltech">
                {events.length > 0 && (
                  <>
                    <div className="w-full">
                      <p className="w-full px-8 font-primary font-normal text-darkgray text-[.8125rem] leading-8">
                        Events
                      </p>
                      {events.map((data, idx: number) => (
                        <EventAutosuggestItem data={data} search={searchText} key={idx} />
                      ))}
                    </div>
                    <div className="w-full px-8 mt-3 mb-2 h-px">
                      <div className="w-full bg-navy opacity-20 h-[1px]" />
                    </div>
                  </>
                )}
              </ThemeOnly>
            </div>
          </div>
        </div>
      )}

      {SHOW_ASK_AI && SHOW_ASK_AI != undefined && checkAskAIPermission ? (
        <>
          <ThemeOnly theme="legaltech">
            <div className={cn('flex flex-row items-center justify-center mt-2')}>
              <div className={cn('flex flex-col w-full')}>
                <div className={cn('rounded-full flex flex-row items-center justify-center w-auto mb-2 text-white')}>
                  <div className={cn('flex flex-row items-center justify-center gap-1')}>
                    <div
                      className={cn(
                        'w-auto rounded-fulltext-white flex flex-none cursor-pointer items-center justify-center mb-2 text-white underline underline-offset-[0.1rem] decoration-white decoration-solid decoration-[.1rem]',
                      )}
                      onClick={() => setIsExpand(!isExpand)}
                    >
                      {!HOMEPAGE_REMOVE_ITEMS ? (
                        !isExpand ? (
                          'LTH Navigator'
                        ) : (
                          'Keyword Search'
                        )
                      ) : (
                        <>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M10 19L7.5 13.5L2 11L7.5 8.5L10 3L12.5 8.5L18 11L12.5 13.5L10 19ZM18 21L16.75 18.25L14 17L16.75 15.75L18 13L19.25 15.75L22 17L19.25 18.25L18 21Z"
                              fill="#29ef97"
                            />
                          </svg>
                          <span className="flex flex-row items-center font-primary font-regular text-[14px] font-[500] leading-[16.37px] text-[#FFFFFF] ml-[4px]">
                            {!isExpand ? 'LTH Navigator' : 'Keyword Search'}
                          </span>
                        </>
                      )}
                    </div>
                    <div
                      className={cn(
                        ' w-auto rounded-fulltext-white flex flex-none cursor-pointer items-center justify-center mb-2 text-white underline underline-offset-[0.1rem] decoration-white decoration-solid decoration-[.1rem]',
                      )}
                    >
                      {!isExpand && (
                        <>
                          <Hidden mdDown>
                            <CustomTooltip
                              id="search-button-3"
                              title={tooltipTitle}
                              marginLeft={1}
                              placement={'right'}
                              fromPage={'homepage'}
                            >
                              <div className="flex items-center cursor-pointer">
                                <IonIcon
                                  className="!w-[16px] !h-[16px] text-turquoise italic"
                                  name="information-circle-outline"
                                />
                              </div>
                            </CustomTooltip>
                          </Hidden>
                          <Hidden mdUp>
                            <CustomTooltip
                              id="search-button-3"
                              title={tooltipTitle}
                              marginLeft={0}
                              placement={'top'}
                              fromPage={'homepage'}
                            >
                              <div className="flex items-center cursor-pointer">
                                <IonIcon
                                  className="!w-[16px] !h-[16px] text-turquoise italic"
                                  name="information-circle-outline"
                                />
                              </div>
                            </CustomTooltip>
                          </Hidden>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ThemeOnly>

          {ASK_AI_NEW_TAB == false && (
            <ThemeOnly theme="legaltech">
              <div className="search-result">
                {loading ? (
                  <>
                    <div className="flex justify-center mb-10">
                      <div className="flex items-center">
                        <TailSpin stroke="#FFF" height="2em" /> <div className="pl-3 text-white"> Loading...</div>
                      </div>
                    </div>
                    <div className="flex justify-center mb-0">
                      {loading && (
                        <LoadingAnimationHr
                          className="flex flex-row space-x-4"
                          result={''}
                          sources={[]}
                        ></LoadingAnimationHr>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {isExpand && tableData && (
                      <LoadingAnimationHr
                        className={`flex flex-row space-x-4 ${sources.length > 3 ? 'premium-search-result-scroll' : ''}`}
                        result={tableData}
                        sources={sources}
                        id={aiSmartSearchId}
                        identicalId={identicalId}
                      ></LoadingAnimationHr>
                    )}
                  </>
                )}
                {!loading && isExpand && errrorMessage && (
                  <div className="alert alert-warning text-center p-3 mb-2 text-white" role="alert">
                    {errrorMessage}
                  </div>
                )}
              </div>
            </ThemeOnly>
          )}
        </>
      ) : (
        ''
      )}
      {SHOW_FREE_ASK_AI && isfreeASKAI != null && isfreeASKAI == false && isNonLogin && (
        <FreeASKAIPopup
          isOpen={nonLoginPopup}
          onClose={() => setNonLoginPopup(false)}
          heading="You’ve used your last free LTH Navigator search."
          title={
            <p>
              Sign up for <span className="text-navybright">premium</span> to access unlimited searches.
            </p>
          }
          createAccountButton={false}
          premiumSignupButton={true}
        />
      )}
      {SHOW_FREE_ASK_AI && isfreeASKAI != null && isfreeASKAI == false && !isNonLogin && (
        <FreeASKAIPopup
          isOpen={loginPopup}
          onClose={() => setLoginPopup(false)}
          heading="You’ve used your last free LTH Navigator search."
          title={
            <p>
              Sign up for <span className="text-navybright">premium</span> to access unlimited searches.
            </p>
          }
          createAccountButton={false}
          premiumSignupButton={true}
        />
      )}
    </>
  )
}
